/**
 * Firebase Services Export Index
 *
 * This file serves as the main entry point for Firebase services.
 * It initializes all Firebase services and exports them for use throughout the application.
 */

// Import Firebase initialization modules
import {
  initializeFirebaseApp,
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseFirestore,
  getInitializedFirestore,
  waitForFirebaseInitialization,
  isFirebaseInitialized,
} from "./core/FirebaseApp";

// Import Firebase services
import * as AuthService from "./AuthService";
import * as CloudFunctionsService from "./CloudFunctionsService";

const isClient = typeof window !== "undefined";

if (isClient) {
  initializeFirebaseApp();
}

/**
 * Initialize all Firebase services
 * @returns {Promise<boolean>} Success status
 */
export async function initializeFirebase() {
  if (!isClient) {
    return false;
  }
  try {
    console.debug("Initializing all Firebase services...");
    // Wait for Firebase app initialization
    await initializeFirebaseApp();

    // Initialize auth and wait for it to complete
    await AuthService.initializeAuthService();

    console.debug("All Firebase services initialized successfully");
    return true;
  } catch (error) {
    console.error("Error initializing Firebase services:", error);
    throw error;
  }
}

/**
 * Clean up all Firebase resources and listeners
 * Call this when the application is shutting down or user is logging out
 */
export function cleanupFirebase() {
  if (!isClient) {
    return false;
  }
  try {
    console.debug("Firebase cleanup completed");
    return true;
  } catch (error) {
    console.error("Error during Firebase cleanup:", error);
    return false;
  }
}

/**
 * Get the Firebase app instance
 * @returns {Object} Firebase app instance
 */
export function getApp() {
  if (!isClient) {
    return null;
  }
  return getFirebaseApp();
}

// Export Firebase Authentication Service
export const Auth = {
  // User Authentication - Async (Reliable)
  waitForAuthReady: AuthService.waitForAuthReady,
  getLoggedInUserAsync: AuthService.getLoggedInUserAsync,
  userIsLoggedInAsync: AuthService.userIsLoggedInAsync,

  // User Authentication - Sync (Legacy - may be unreliable during auth initialization)
  userIsLoggedIn: AuthService.userIsLoggedIn,
  getLoggedInUser: AuthService.getLoggedInUser,

  // Token and ID Methods
  getUserIdToken: AuthService.getUserIdToken,
  getUid: AuthService.getUid,
  getAnonymousTokenId: AuthService.getAnonymousTokenId,

  // Authentication Methods
  logInAnonymously: AuthService.logInAnonymously,
  logOut: AuthService.logOut,
  // Redirect result processing (used in boot flows)
  processRedirectResult: AuthService.processRedirectResult,

  // Auth State Management
  addAuthStateListener: AuthService.addAuthStateListener,
};

// Export Cloud Functions Service
export const CloudFunctions = {
  // Wheel Sharing Functions
  createSharedWheel: CloudFunctionsService.createSharedWheel,
  logSharedWheelRead: CloudFunctionsService.logSharedWheelRead,
  getSharedWheel: CloudFunctionsService.getSharedWheel,
  getSharedWheels: CloudFunctionsService.getSharedWheels,
  deleteSharedWheel: CloudFunctionsService.deleteSharedWheel,

  // Management Token Functions (for unauthenticated users)
  getSharedWheelsByTokens: CloudFunctionsService.getSharedWheelsByTokens,
  deleteSharedWheelByToken: CloudFunctionsService.deleteSharedWheelByToken,

  // User Data Management Functions (authenticated users)
  getUserProfile: CloudFunctionsService.getUserProfile,
  upsertUserProfile: CloudFunctionsService.upsertUserProfile,
  getUserWheels: CloudFunctionsService.getUserWheels,
  saveUserWheel: CloudFunctionsService.saveUserWheel,
  deleteUserWheel: CloudFunctionsService.deleteUserWheel,
  logUserActivity: CloudFunctionsService.logUserActivity,
  logWheelRead: CloudFunctionsService.logWheelRead,

  // User Token Management Functions (authenticated users)
  getUserTokens: CloudFunctionsService.getUserTokens,
  addUserToken: CloudFunctionsService.addUserToken,
  removeUserToken: CloudFunctionsService.removeUserToken,

  // User Management
  convertAccount: CloudFunctionsService.convertAccount,
  deleteAccount: CloudFunctionsService.deleteAccount,
  userIsAdmin: CloudFunctionsService.userIsAdmin,

  // Admin Management Functions
  getAdmins: CloudFunctionsService.getAdmins,
  updateAdmin: CloudFunctionsService.updateAdmin,
  deleteAdmin: CloudFunctionsService.deleteAdmin,
  addAdmin: CloudFunctionsService.addAdmin,
  setAdminsWheelsToZero: CloudFunctionsService.setAdminsWheelsToZero,
  getPayments: CloudFunctionsService.getPayments,
  processSharedWheelReads: CloudFunctionsService.processSharedWheelReads,

  // Admin Wheel Review Functions
  approveSharedWheelForReview:
    CloudFunctionsService.approveSharedWheelForReview,
  rejectSharedWheelForReview: CloudFunctionsService.rejectSharedWheelForReview,

  // Content Management
  getCarousels: CloudFunctionsService.getCarousels,
  saveCarousel: CloudFunctionsService.saveCarousel,
  getNumberOfWheelsInReviewQueue:
    CloudFunctionsService.getNumberOfWheelsInReviewQueue,
  translate: CloudFunctionsService.translate,

  // Settings Operations (admin-only)
  getDirtyWords: CloudFunctionsService.getDirtyWords,
  setDirtyWords: CloudFunctionsService.setDirtyWords,
  getEarningsPerReview: CloudFunctionsService.getEarningsPerReview,

  // Admin Session Management
  resetSessionReviews: CloudFunctionsService.resetSessionReviews,
  getNextSharedWheelForReview:
    CloudFunctionsService.getNextSharedWheelForReview,
};

// Export initialization utilities
export const Initialization = {
  // Core initialization
  waitForFirebaseInitialization: waitForFirebaseInitialization,
  isFirebaseInitialized: isFirebaseInitialized,

  // Initialize all services
  initializeFirebase: initializeFirebase,

  // Cleanup
  cleanupFirebase: cleanupFirebase,
};

// The exports above provide clean access to all Firebase functionality
// Each service is properly namespaced to avoid collisions
// All Firebase operations now go through secure CloudFunctions for better security
